import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Play, Home, Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';

const API_BASE = 'https://watchnow.panguplay.workers.dev';
const MOVIES_PER_PAGE = 20;
const HOME_MOVIES_LIMIT = 10;

export default function WatchNow() {
  const [view, setView] = useState('home');
  const [sections, setSections] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [displayedMovies, setDisplayedMovies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const observerRef = useRef();
  const lastMovieRef = useRef();

  useEffect(() => {
    loadHome();
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    if (view !== 'category' || !selectedSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && displayedMovies.length < selectedSection.movies.length) {
          loadMoreMovies();
        }
      },
      { threshold: 0.5 }
    );

    observerRef.current = observer;

    if (lastMovieRef.current) {
      observer.observe(lastMovieRef.current);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [view, displayedMovies, selectedSection]);

  const loadHome = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/home`);
      const data = await res.json();
      // Limit movies to 10 per section for home view
      const limitedSections = data.sections.map(section => ({
        ...section,
        movies: section.movies.slice(0, HOME_MOVIES_LIMIT),
        totalMovies: section.movies.length
      }));
      setSections(limitedSections);
      // Store full sections for later use
      window.fullSections = data.sections;
      setView('home');
      setMenuOpen(false);
    } catch (err) {
      console.error('Failed to load home:', err);
    }
    setLoading(false);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setMenuOpen(false);
    try {
      const res = await fetch(`${API_BASE}/search/${encodeURIComponent(searchQuery)}`);
      const data = await res.json();
      setSearchResults(data.results || []);
      setView('search');
    } catch (err) {
      console.error('Search failed:', err);
    }
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const loadMovie = async (slug) => {
    setLoading(true);
    setMenuOpen(false);
    try {
      const res = await fetch(`${API_BASE}/movie/${slug}`);
      const data = await res.json();
      setSelectedMovie(data.data);
      setView('player');
    } catch (err) {
      console.error('Failed to load movie:', err);
    }
    setLoading(false);
  };

  const viewAllMovies = (sectionTitle) => {
    // Get full section data
    const fullSection = window.fullSections?.find(s => s.title === sectionTitle);
    if (!fullSection) return;

    setSelectedSection(fullSection);
    setDisplayedMovies(fullSection.movies.slice(0, MOVIES_PER_PAGE));
    setView('category');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const loadMoreMovies = () => {
    if (loadingMore || !selectedSection) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      const currentLength = displayedMovies.length;
      const nextMovies = selectedSection.movies.slice(
        currentLength, 
        currentLength + MOVIES_PER_PAGE
      );
      setDisplayedMovies(prev => [...prev, ...nextMovies]);
      setLoadingMore(false);
    }, 300);
  };

  const scrollCategory = (direction, index) => {
    const container = document.getElementById(`category-${index}`);
    if (container) {
      const scrollAmount = 800;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const MovieCard = ({ movie, isLast }) => (
    <div 
      ref={isLast ? lastMovieRef : null}
      className="flex-shrink-0 w-40 cursor-pointer group"
      onClick={() => loadMovie(movie.slug)}
    >
      <div className="relative overflow-hidden rounded-lg bg-gray-800 transition-transform group-hover:scale-105">
        <img 
          src={movie.poster} 
          alt={movie.title}
          className="w-full h-60 object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-opacity flex items-center justify-center">
          <Play className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={48} />
        </div>
      </div>
      <h3 className="mt-2 text-sm font-medium text-white truncate">{movie.title}</h3>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-900 bg-opacity-95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 
              className="text-2xl font-bold text-red-600 cursor-pointer"
              onClick={loadHome}
            >
              WatchNow
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Search movies..."
                className="w-full bg-gray-800 text-white pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
              />
            </div>

            <button 
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Menu Dropdown */}
        {menuOpen && (
          <div className="absolute top-full right-0 mt-0 w-64 bg-gray-800 border-l border-b border-gray-700 shadow-lg max-h-96 overflow-y-auto">
            <nav className="flex flex-col">
              <button 
                className="flex items-center gap-2 px-4 py-3 hover:bg-gray-700 transition-colors text-left border-b border-gray-700"
                onClick={loadHome}
              >
                <Home size={20} />
                Home
              </button>
              {sections.map((section, idx) => (
                <button
                  key={idx}
                  className="px-4 py-3 hover:bg-gray-700 transition-colors text-left border-b border-gray-700"
                  onClick={() => viewAllMovies(section.title)}
                >
                  {section.title}
                  {section.totalMovies > HOME_MOVIES_LIMIT && (
                    <span className="text-xs text-gray-400 ml-2">
                      ({section.totalMovies})
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-8">
        {loading && (
          <div className="flex items-center justify-center h-96">
            <div className="text-2xl text-gray-400">Loading...</div>
          </div>
        )}

        {/* Movie Player View */}
        {view === 'player' && selectedMovie && !loading && (
          <div className="max-w-7xl mx-auto px-4">
            <button 
              onClick={loadHome}
              className="mb-4 text-gray-400 hover:text-white transition-colors"
            >
              ← Back to Home
            </button>
            
            {selectedMovie.playlist && (
              <div className="bg-black rounded-lg overflow-hidden mb-6">
                <video 
                  controls 
                  className="w-full aspect-video"
                  src={selectedMovie.playlist}
                  poster={selectedMovie.poster}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <h1 className="text-3xl font-bold mb-2">{selectedMovie.title}</h1>
                
                <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-400">
                  {selectedMovie.releaseYear && <span>{selectedMovie.releaseYear}</span>}
                  {selectedMovie.durationFormatted && <span>{selectedMovie.durationFormatted}</span>}
                  {selectedMovie.rating && <span>⭐ {selectedMovie.rating}</span>}
                </div>

                {selectedMovie.genres && selectedMovie.genres.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedMovie.genres.map((genre, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-800 rounded-full text-sm capitalize">
                        {genre}
                      </span>
                    ))}
                  </div>
                )}

                {selectedMovie.description && (
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold mb-2">Description</h2>
                    <p className="text-gray-300">{selectedMovie.description}</p>
                  </div>
                )}

                {selectedMovie.review && (
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold mb-2">Review</h2>
                    <p className="text-gray-300">{selectedMovie.review}</p>
                  </div>
                )}
              </div>

              <div>
                {selectedMovie.director && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-400 mb-1">Director</h3>
                    <p>{selectedMovie.director}</p>
                  </div>
                )}

                {selectedMovie.cast && selectedMovie.cast.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-400 mb-2">Cast</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedMovie.cast.map((actor, i) => (
                        <span key={i} className="text-sm text-gray-300">
                          {actor}{i < selectedMovie.cast.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Category View All */}
        {view === 'category' && selectedSection && !loading && (
          <div className="max-w-7xl mx-auto px-4">
            <button 
              onClick={loadHome}
              className="mb-4 text-gray-400 hover:text-white transition-colors"
            >
              ← Back to Home
            </button>
            <h2 className="text-3xl font-bold mb-2">{selectedSection.title}</h2>
            <p className="text-gray-400 mb-2">{selectedSection.description}</p>
            <p className="text-sm text-gray-500 mb-6">
              Showing {displayedMovies.length} of {selectedSection.movies.length} movies
            </p>
            
            {/* Mobile: 3 columns with padding, Desktop: 6 columns */}
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 px-2 sm:px-0">
              {displayedMovies.map((movie, index) => (
                <div
                  key={movie.slug}
                  ref={index === displayedMovies.length - 1 ? lastMovieRef : null}
                  className="cursor-pointer group"
                  onClick={() => loadMovie(movie.slug)}
                >
                  <div className="relative overflow-hidden rounded-lg bg-gray-800 transition-transform group-hover:scale-105">
                    <img 
                      src={movie.poster} 
                      alt={movie.title}
                      className="w-full aspect-[2/3] object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-opacity flex items-center justify-center">
                      <Play className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                    </div>
                  </div>
                  <h3 className="mt-2 text-xs sm:text-sm font-medium text-white truncate">{movie.title}</h3>
                </div>
              ))}
            </div>

            {loadingMore && (
              <div className="flex justify-center py-8">
                <div className="text-gray-400">Loading more...</div>
              </div>
            )}

            {displayedMovies.length >= selectedSection.movies.length && (
              <div className="text-center py-8 text-gray-400">
                All movies loaded ({selectedSection.movies.length} total)
              </div>
            )}
          </div>
        )}

        {/* Search Results View */}
        {view === 'search' && !loading && (
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6">
              Search Results for "{searchQuery}" ({searchResults.length})
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 px-2 sm:px-0">
              {searchResults.map((movie) => (
                <div
                  key={movie.slug}
                  className="cursor-pointer group"
                  onClick={() => loadMovie(movie.slug)}
                >
                  <div className="relative overflow-hidden rounded-lg bg-gray-800 transition-transform group-hover:scale-105">
                    <img 
                      src={movie.poster} 
                      alt={movie.title}
                      className="w-full aspect-[2/3] object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-opacity flex items-center justify-center">
                      <Play className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                    </div>
                  </div>
                  <h3 className="mt-2 text-xs sm:text-sm font-medium text-white truncate">{movie.title}</h3>
                </div>
              ))}
            </div>
            {searchResults.length === 0 && (
              <div className="text-center text-gray-400 py-12">
                No movies found. Try a different search term.
              </div>
            )}
          </div>
        )}

        {/* Home View */}
        {view === 'home' && !loading && (
          <div className="max-w-7xl mx-auto px-4">
            {sections.map((section, idx) => (
              <div key={idx} className="mb-12">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold">{section.title}</h2>
                    <p className="text-gray-400 text-sm">{section.description}</p>
                  </div>
                  {section.totalMovies > HOME_MOVIES_LIMIT && (
                    <button
                      onClick={() => viewAllMovies(section.title)}
                      className="text-red-500 hover:text-red-400 font-medium text-sm flex items-center gap-1 whitespace-nowrap"
                    >
                      View All ({section.totalMovies}) →
                    </button>
                  )}
                </div>
                
                <div className="relative group">
                  {/* Left Arrow */}
                  <button
                    onClick={() => scrollCategory('left', idx)}
                    className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black bg-opacity-70 hover:bg-opacity-90 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft size={24} />
                  </button>

                  {/* Movies Container */}
                  <div 
                    id={`category-${idx}`}
                    className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
                  >
                    {section.movies.map((movie) => (
                      <MovieCard key={movie.slug} movie={movie} />
                    ))}
                  </div>

                  {/* Right Arrow */}
                  <button
                    onClick={() => scrollCategory('right', idx)}
                    className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black bg-opacity-70 hover:bg-opacity-90 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}